import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { isReviewAccount } from "@/lib/review/reviewApi";

const cache = new Map<string, boolean>();

/**
 * Server-side classification of the signed-in user as the isolated App Review account.
 * Callers must wait while loading and fail closed on error.
 */
export function useReviewAccount() {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ key: string; value: boolean | null; error: boolean }>(
    { key: "", value: null, error: false },
  );
  const key = `${uid}:${attempt}`;

  useEffect(() => {
    if (!uid) return;
    if (cache.has(uid)) { setState({ key, value: cache.get(uid)!, error: false }); return; }
    let alive = true;
    setState({ key, value: null, error: false });
    Promise.resolve()
      .then(isReviewAccount)
      .then((v) => { cache.set(uid, v); if (alive) setState({ key, value: v, error: false }); })
      .catch(() => { if (alive) setState({ key, value: null, error: true }); });
    return () => { alive = false; };
  }, [uid, key]);

  const retry = useCallback(() => {
    if (uid) cache.delete(uid);
    setAttempt((n) => n + 1);
  }, [uid]);

  if (!uid) return { isReview: false, isLoading: false, isError: false, retry };
  const cached = cache.get(uid);
  const current = state.key === key ? state : { value: cached ?? null, error: false };
  return {
    isReview: current.value === true,
    isLoading: current.value === null && !current.error,
    isError: current.error,
    retry,
  };
}
